import {Sound, filters} from '@pixi/sound';
import {ISoundPropertiesConfig} from '@gongxigames/framework/slot/types';
import {consoleLogger} from '@gongxigames/framework/core';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {SoundPlayer} from '@gongxigames/framework/slot/services/SoundPlayer';

const addEventListenerToButton = function(id: string, listener: () => void): void {
    const btn = document.getElementById(id)!;

    btn.addEventListener('click', listener);
};

export class SoundsToolsPage {
    protected _uploadSoundInput: HTMLInputElement;
    protected _uploadSoundFilesButton: HTMLInputElement;
    protected _soundSelect: HTMLSelectElement;
    protected _soundVolumeView: HTMLElement;
    protected _panningValueVew: HTMLElement;
    protected _soundVolumeRange: HTMLInputElement;
    protected _soundPanRange: HTMLInputElement;

    protected _soundManager: SoundManager;
    protected _soundPlayer: SoundPlayer;

    protected _soundsConfig: any = {};
    protected _sounds: any = {};
    protected _currentSound!: string;
    protected _gameName: string;

    constructor(data: any) {
        this._sounds = data.sounds;
        this._soundsConfig = data.soundsConfig;
        this._soundPlayer = data.soundPlayer;
        this._soundManager = data.soundManager;
        this._gameName = data.gameName;

        this._soundVolumeView = document.getElementById('volume-level-value')!;
        this._panningValueVew = document.getElementById('pan-level-value')!;

        this._soundVolumeRange = document.getElementById('volume') as HTMLInputElement;
        this._soundVolumeRange.addEventListener('change', this.onChangeVolume.bind(this));
        this._soundVolumeRange.addEventListener('input', this.onChangeVolume.bind(this));

        this._soundPanRange = document.getElementById('pan') as HTMLInputElement;
        this._soundPanRange.addEventListener('change', this.onChangepanning.bind(this));
        this._soundPanRange.addEventListener('input', this.onChangepanning.bind(this));

        this._uploadSoundInput = document.getElementById('file-input') as HTMLInputElement;
        this._uploadSoundInput.addEventListener('change', this.onSelectFile.bind(this), false);

        const dropContainer = document.getElementById('dropContainer')!;

        dropContainer.ondragover = dropContainer.ondragenter = evt => evt.preventDefault();

        dropContainer.ondrop = (env: DragEvent) => {
            this.uploadFiles(env.dataTransfer!.files);
            env.preventDefault();
        };

        this._uploadSoundFilesButton = document.getElementById('files-input') as HTMLInputElement;
        // this._uploadSoundFilesButton.addEventListener('change', this.uploadFiles.bind(this), false);

        this._soundSelect = document.getElementById('sound-select') as HTMLSelectElement;
        this._soundSelect.addEventListener('change', () => this.selectSound(this._soundSelect.value));

        addEventListenerToButton('playSound', this.playCurrentSound.bind(this));
        addEventListenerToButton('stopSound', this.stopCurrentSound.bind(this));
        addEventListenerToButton('stopAll', this.stopAllSounds.bind(this));
        addEventListenerToButton('save-sounds-configs', this.saveConfigToFile.bind(this));

        const sortedListOfSounds = Object.keys(data.sounds).sort();

        sortedListOfSounds.forEach(soundName => {
            const option = document.createElement('option');

            option.value = soundName;
            option.text = soundName;
            this._soundSelect.add(option);
        });

        this.selectSound(sortedListOfSounds[0]);
    }

    protected onSelectFile(e: any): void {
        if (e.target.files.length) {
            const name = this._soundSelect[this._soundSelect.selectedIndex].innerText;

            this.loadFile(e.target.files[0])
                .then( ({sound}) => this.replaceSound(name, sound))
                .then( () => this._uploadSoundInput.value = '' );
        }
    }

    protected uploadFiles(files: FileList): void {
        for (const file of Array.from(files)) {
            this.loadFile(file)
                .then(({name, sound}) => this.replaceSound(name, sound));
        }
    }

    protected playCurrentSound(): void {
        const id = this.currentSoundID;

        if (this._soundManager.hasMusic(id)) {
            this._soundPlayer.playMusic(this.currentSoundID);
        } else {
            this._soundPlayer.play(this.currentSoundID);
        }
    }

    protected stopCurrentSound(): void {
        const id = this.currentSoundID;

        if (this._soundManager.hasMusic(id)) {
            this._soundPlayer.stopMusic();
        } else {
            this._soundPlayer.stop(this.currentSoundID);
        }
    }

    protected stopAllSounds(): void {
        for (const name in this._sounds) {
            if (this._sounds.hasOwnProperty(name)) {
                const id = this._soundManager.getSoundIdFromName(name)!;

                if (this._soundManager.hasMusic(id) && this._soundPlayer.getPlayingMusicID() === id) {
                    this._soundPlayer.stopMusic();
                } else {
                    this._soundPlayer.stop(id);
                }
            }
        }
    }

    protected onChangeVolume(): void {
        const volume = this._soundVolumeRange.valueAsNumber;
        const config: ISoundPropertiesConfig = this._soundsConfig[this._currentSound];
        const id = this._soundManager.getSoundIdFromName(this._currentSound)!;

        config.volume = volume;
        this.currentSound.volume = volume;
        this.soundVolumeText = volume;

        if (this._soundManager.hasSound(id)) {
            this._soundManager.getSound(id).volume = volume;
        } else if (this._soundManager.hasMusic(this._currentSound)) {
            this._soundManager.getMusic(id).volume = volume;
        }
    }

    protected onChangepanning(): void {
        const panning = this._soundPanRange.valueAsNumber;
        const config: ISoundPropertiesConfig = this._soundsConfig[this._currentSound];
        const id = this._soundManager.getSoundIdFromName(this._currentSound)!;

        config.panning = panning;
        this.currentSound.filters = [new filters.StereoFilter(panning)];
        this.soundpanningText = panning;

        if (this._soundManager.hasSound(id)) {
            this._soundManager.getSound(id).filters = this.currentSound.filters;
        } else if (this._soundManager.hasMusic(id)) {
            this._soundManager.getMusic(id).filters = this.currentSound.filters;
        }
    }

    protected saveConfigToFile(): void {
        const fileName = `${this._gameName}-sounds.rls`;
        const data = {high: {children: [] as any}};

        Object.keys(this._soundsConfig).forEach( name => {
            const {
                volume,
                panning = 0,
                sourcePath: path,
                loop,
                priority,
                classifier = '',
                duck = []
            } = this._soundsConfig[name];

            data.high.children.push({
                panning: Math.round(panning * 100),
                volume: Math.round(volume * 100),
                path,
                loop,
                duck,
                classifier,
                priority
            });
        });

        const a = document.createElement('a');
        const file = new Blob([JSON.stringify(data, null, 4)], {type: 'text/plain'});

        a.href = URL.createObjectURL(file);
        a.download = fileName;
        a.click();
    }

    protected selectSound(soundName: string): void {
        this._currentSound = soundName;

        const config: ISoundPropertiesConfig = this._soundsConfig[soundName];

        if (config) {
            this.soundVolumeText = config.volume;
            this.soundpanningText = config.panning || 0;
            this._soundVolumeRange.valueAsNumber = config.volume;
            this._soundPanRange.valueAsNumber = config.panning || 0;
        } else {
            consoleLogger.warn(`Did not found sound config ${soundName}`);
        }
    }

    protected loadFile(file: File): Promise<{name: string; sound: Sound;}> {
        return new Promise<any>(resolve => {
            const reader: FileReader = new FileReader();
            const name = file.name.split('.')[0];

            reader.onload = (e) => {
                const source: ArrayBuffer = ((e.target as FileReader).result as ArrayBuffer);
                const sound = Sound.from({source});

                resolve({name, sound});
            };
            reader.readAsArrayBuffer(file);
        });
    }

    protected replaceSound(name: string, sound: Sound): void {
        const sm: SoundManager = this._soundManager;
        const sp: SoundPlayer = this._soundPlayer;
        const id = sm.getSoundIdFromName(name)!;

        if (id) {
            sound.url = name;
        }

        if (sm.hasMusic(id)) {
            if (sp.getPlayingMusicID() === id) {
                sp.stopMusic();
                sm.setMusic(id, sound);
                sp.playMusic(id);
            } else {
                sm.setMusic(id, sound);
            }

            consoleLogger.log(`${id} has been replaced in music`);
        } else if (sm.hasSound(id)) {
            sm.setSound(id, sound);
            consoleLogger.log(`${id} has been replaced in sounds`);
        } else {
            consoleLogger.warn(`No sound/music with id=${id} was found`);
        }
    }

    protected set soundVolumeText(value: string | number) {
        this._soundVolumeView.textContent = String(value);
    }

    protected set soundpanningText(value: string | number) {
        this._panningValueVew.textContent = String(value);
    }

    get currentSoundID(): string {
        return this._soundManager.getSoundIdFromName(this._currentSound)!;
    }

    get currentSound(): Sound {
        const sm = this._soundManager;

        return sm.hasMusic(this.currentSoundID) ? sm.getMusic(this.currentSoundID) : sm.getSound(this.currentSoundID);
    }

}
